import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "product-catalogs-container bsp-page" }
const _hoisted_2 = {
  key: 0,
  class: "content bsp-page__content"
}
const _hoisted_3 = {
  class: "bsp-summary-grid",
  "aria-label": "Catalog summary"
}
const _hoisted_4 = {
  class: "bsp-section bsp-catalog-guidance",
  "aria-labelledby": "catalog-permissions-title"
}
const _hoisted_5 = { class: "bsp-catalog-guidance__grid" }
const _hoisted_6 = { class: "bsp-catalog-guidance__item" }
const _hoisted_7 = { class: "bsp-catalog-guidance__item" }
const _hoisted_8 = {
  class: "bsp-section",
  "aria-labelledby": "catalog-access-title"
}
const _hoisted_9 = { class: "bsp-section-heading bsp-section-heading--toolbar" }
const _hoisted_10 = {
  key: 0,
  role: "status"
}
const _hoisted_11 = {
  key: 1,
  role: "alert",
  class: "bsp-form-error"
}
const _hoisted_12 = {
  key: 2,
  role: "status"
}
const _hoisted_13 = ["href"]
const _hoisted_14 = {
  key: 0,
  class: "bsp-catalog-notice",
  role: "status",
  "aria-labelledby": "catalog-read-only-title"
}
const _hoisted_15 = {
  key: 1,
  class: "bsp-section",
  "aria-labelledby": "catalog-visibility-title"
}
const _hoisted_16 = { class: "bsp-section-heading" }
const _hoisted_17 = ["aria-busy"]
const _hoisted_18 = { class: "bsp-section-heading bsp-section-heading--toolbar" }
const _hoisted_19 = { class: "bsp-section-heading__title-row" }
const _hoisted_20 = {
  key: 2,
  class: "bsp-catalog-grid"
}
const _hoisted_21 = { class: "bsp-catalog-card__header" }
const _hoisted_22 = { class: "bsp-catalog-card__identity" }
const _hoisted_23 = { class: "bsp-catalog-card__tags" }
const _hoisted_24 = { class: "bsp-catalog-details" }
const _hoisted_25 = { class: "bsp-catalog-card__actions" }
const _hoisted_26 = {
  key: 0,
  class: "bsp-catalog-card__help"
}
const _hoisted_27 = {
  key: 1,
  class: "bsp-catalog-card__help"
}
const _hoisted_28 = {
  key: 1,
  class: "bsp-empty-page"
}
const _hoisted_29 = { class: "bsp-catalog-fixed-field" }
const _hoisted_30 = {
  key: 0,
  class: "bsp-catalog-error",
  role: "alert"
}
const _hoisted_31 = { class: "bsp-modal-form" }
const _hoisted_32 = {
  class: "bsp-catalog-notice bsp-catalog-notice--compact",
  role: "status"
}
const _hoisted_33 = {
  key: 0,
  class: "bsp-catalog-error",
  role: "alert"
}
const _hoisted_34 = { class: "bsp-modal-form" }
const _hoisted_35 = {
  key: 0,
  class: "bsp-catalog-error",
  role: "alert"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { POSITION, useToast } from 'vue-toastification'
import {
  AButton,
  AEmptyViewCard,
  AIcon,
  AInformationCard,
  AInput,
  AModal,
  ATag
} from '@adereso-ai/vue-components'
import BspPageHeader from '@/components/BspPageHeader.vue'
import CatalogVisibilitySettings from '@/components/CatalogVisibilitySettings.vue'
import SpinnerLoader from '@/components/loaders/SpinnerLoader.vue'
import AccountService from '@/services/AccountService/AccountService'
import { WhatsappBusinessAccount } from '@/services/AccountService/types'
import AuthService from '@/services/AuthService/AuthService'
import { User } from '@/services/AuthService/types'
import MetaService from '@/services/MetaService/MetaService'
import { ProductCatalog } from '@/services/MetaService/types'
import { CatalogLoginSdk, requestCatalogAuthorization } from '@/utils/catalogAuthorization'

declare const FB: CatalogLoginSdk

type CatalogAction = 'edit' | 'connect' | 'disconnect' | 'authorize'


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCatalogsView',
  setup(__props) {

const CATALOG_MANAGER_ROLES = new Set(['admin', 'superuser'])

const router = useRouter()
const route = useRoute()
const toast = useToast()

function rememberCatalogPhone (phoneId: string) {
  if (route.query.phoneNumberId !== phoneId) {
    router.replace({ query: { ...route.query, phoneNumberId: phoneId } })
  }
}

const loading = ref(true)
const refreshing = ref(false)
const loadError = ref('')
const catalogAuthorizationError = ref('')
const catalogAuthorizationConfigId = process.env.VUE_APP_FACEBOOK_CATALOG_CONFIG_ID || ''
let authorizationAbort: AbortController | undefined
let pageActive = true
onBeforeUnmount(() => {
  pageActive = false
  authorizationAbort?.abort()
})
const currentUser = ref<User | undefined>(undefined)
const whatsappBusinessAccount = ref<WhatsappBusinessAccount | undefined>(undefined)
const catalogs = ref<ProductCatalog[]>([])

const formModalOpen = ref(false)
const catalogBeingEdited = ref<ProductCatalog | undefined>(undefined)
const catalogName = ref('')
const formError = ref('')

const catalogToConnect = ref<ProductCatalog | undefined>(undefined)
const connectModalOpen = ref(false)
const connectError = ref('')

const catalogToDisconnect = ref<ProductCatalog | undefined>(undefined)
const disconnectModalOpen = ref(false)
const disconnectError = ref('')

const activeAction = ref<CatalogAction | undefined>(undefined)
const isMutating = computed(() => activeAction.value !== undefined)
const canManageCatalogs = computed(() => {
  return currentUser.value?.roles?.some(role => CATALOG_MANAGER_ROLES.has(role)) ?? false
})
const writesBlocked = computed(() => {
  return !canManageCatalogs.value || loading.value || refreshing.value || isMutating.value
})

const metaCatalogAccessUrl = computed(() => {
  const base = 'https://business.facebook.com/latest/settings/connected_apps/'
  const businessId = whatsappBusinessAccount.value?.owner_business_info?.id
  return businessId ? `${base}?business_id=${encodeURIComponent(businessId)}` : base
})
const associatedCatalog = computed(() => catalogs.value.find(catalog => catalog.is_associated))
const catalogCountLabel = computed(() => {
  return `${catalogs.value.length} catalog${catalogs.value.length === 1 ? '' : 's'}`
})
const catalogBreadcrumbs = computed(() => [
  {
    title: 'WhatsApp Accounts',
    icon: 'tabler:brand-whatsapp',
    to: { name: 'whatsapp-business-accounts' }
  },
  {
    title: whatsappBusinessAccount.value?.name || 'Account',
    to: { name: 'whatsapp-business-account', params: { wabaId: String(route.params.wabaId) } }
  },
  { title: 'Product catalogs', disabled: true }
])
const normalizedCatalogName = computed(() => catalogName.value.trim())
const canSubmitCatalog = computed(() => normalizedCatalogName.value.length > 0)
const catalogFormButtonLabel = computed(() => {
  if (activeAction.value === 'edit') return 'Saving…'
  return 'Save changes'
})

function getErrorMessage (error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

function sortCatalogs (items: ProductCatalog[]): ProductCatalog[] {
  return [...items].sort((first, second) => first.name.localeCompare(second.name))
}

function replaceCatalog (updatedCatalog: ProductCatalog) {
  catalogs.value = sortCatalogs(
    catalogs.value.map(catalog => catalog.id === updatedCatalog.id ? updatedCatalog : catalog)
  )
}

function formatCount (count: number | null): number | string {
  return count === null ? 'Unknown' : count
}

function formatVertical (vertical: string): string {
  if (vertical.toLowerCase() === 'commerce') return 'Commerce'
  return vertical
}

function isCatalogOwned (catalog: ProductCatalog): boolean {
  return catalog.is_owned === true
}

function canStartCatalogMutation (): boolean {
  return canManageCatalogs.value && !loading.value && !refreshing.value && !isMutating.value
}

function isConnectDisabled (catalog: ProductCatalog): boolean {
  return writesBlocked.value ||
    !isCatalogOwned(catalog) ||
    catalog.is_associated ||
    Boolean(associatedCatalog.value)
}

function toastError (message: string) {
  toast.error(message, { position: POSITION.BOTTOM_LEFT })
}

function toastSuccess (message: string) {
  toast.success(message, { position: POSITION.BOTTOM_LEFT })
}

async function getCurrentUser (): Promise<boolean> {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (!currentUser.value) {
    await router.push({ name: 'login', query: route.query })
    return false
  }
  return true
}

async function fetchCatalogs (wabaId: string): Promise<ProductCatalog[]> {
  const metaService = await MetaService.getInstance()
  return sortCatalogs(await metaService.getProductCatalogs(wabaId))
}

async function loadPage () {
  loading.value = true
  loadError.value = ''

  try {
    const authenticated = await getCurrentUser()
    if (!authenticated) return

    const wabaId = route.params.wabaId as string
    if (!wabaId) {
      await router.push({ name: 'home' })
      return
    }

    const accountService = await AccountService.getInstance()
    const [account, productCatalogs] = await Promise.allSettled([
      accountService.getWhatsappBusinessAccount(wabaId),
      fetchCatalogs(wabaId)
    ])
    if (account.status === 'rejected') throw account.reason
    whatsappBusinessAccount.value = account.value
    if (productCatalogs.status === 'rejected') throw productCatalogs.reason
    catalogs.value = productCatalogs.value
  } catch (error) {
    loadError.value = getErrorMessage(error)
    toastError('Error loading product catalogs: ' + loadError.value)
  } finally {
    loading.value = false
  }
}

async function authorizeCatalogs () {
  if (!canStartCatalogMutation() || !catalogAuthorizationConfigId) return
  const wabaId = String(route.params.wabaId || '')
  if (!wabaId) return
  activeAction.value = 'authorize'
  catalogAuthorizationError.value = ''
  authorizationAbort = new AbortController()
  try {
    const code = await requestCatalogAuthorization(
      typeof FB === 'undefined' ? undefined : FB,
      catalogAuthorizationConfigId, authorizationAbort.signal
    )
    if (!code || !pageActive || String(route.params.wabaId) !== wabaId) return
    const metaService = await MetaService.getInstance()
    const updatedCatalogs = await metaService.authorizeProductCatalogs(wabaId, code)
    if (!pageActive || String(route.params.wabaId) !== wabaId) return
    catalogs.value = sortCatalogs(updatedCatalogs)
    loadError.value = ''
    toastSuccess('Catalog access updated. Choose a catalog to connect to this WABA.')
  } catch (error) {
    if (!pageActive || String(route.params.wabaId) !== wabaId) return
    catalogAuthorizationError.value = getErrorMessage(error)
    toastError('Catalog authorization failed: ' + catalogAuthorizationError.value)
  } finally {
    authorizationAbort = undefined
    activeAction.value = undefined
  }
}

async function refreshCatalogs () {
  const wabaId = route.params.wabaId as string
  if (!wabaId || refreshing.value || isMutating.value) return

  refreshing.value = true
  loadError.value = ''
  try {
    catalogs.value = await fetchCatalogs(wabaId)
  } catch (error) {
    loadError.value = getErrorMessage(error)
    toastError('Error refreshing product catalogs: ' + loadError.value)
  } finally {
    refreshing.value = false
  }
}

function openEditModal (catalog: ProductCatalog) {
  if (!canStartCatalogMutation() || !isCatalogOwned(catalog)) return

  catalogBeingEdited.value = catalog
  catalogName.value = catalog.name
  formError.value = ''
  formModalOpen.value = true
}

function closeFormModal () {
  if (isMutating.value) return
  formModalOpen.value = false
  catalogBeingEdited.value = undefined
  catalogName.value = ''
  formError.value = ''
}

async function submitCatalogForm () {
  if (!canStartCatalogMutation() || !canSubmitCatalog.value) return

  const wabaId = route.params.wabaId as string
  if (!wabaId) return

  const catalogToUpdate = catalogBeingEdited.value
  if (!catalogToUpdate || !isCatalogOwned(catalogToUpdate)) return

  let succeeded = false
  activeAction.value = 'edit'
  formError.value = ''

  try {
    const metaService = await MetaService.getInstance()
    const updatedCatalog = await metaService.updateProductCatalog(
      wabaId,
      catalogToUpdate.id,
      { name: normalizedCatalogName.value }
    )
    replaceCatalog(updatedCatalog)
    toastSuccess('Product catalog updated')
    succeeded = true
  } catch (error) {
    formError.value = getErrorMessage(error)
    toastError('Error saving product catalog: ' + formError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeFormModal()
  }
}

function openConnectModal (catalog: ProductCatalog) {
  if (
    !canStartCatalogMutation() ||
    !isCatalogOwned(catalog) ||
    catalog.is_associated ||
    associatedCatalog.value
  ) return

  catalogToConnect.value = catalog
  connectError.value = ''
  connectModalOpen.value = true
}

function closeConnectModal () {
  if (isMutating.value) return
  connectModalOpen.value = false
  catalogToConnect.value = undefined
  connectError.value = ''
}

async function connectCatalog () {
  const selectedCatalog = catalogToConnect.value
  if (
    !canStartCatalogMutation() ||
    !selectedCatalog ||
    !isCatalogOwned(selectedCatalog) ||
    selectedCatalog.is_associated ||
    associatedCatalog.value
  ) return

  const wabaId = route.params.wabaId as string
  if (!wabaId) return

  const catalogId = selectedCatalog.id
  let succeeded = false
  activeAction.value = 'connect'
  connectError.value = ''

  try {
    const metaService = await MetaService.getInstance()
    const connectedCatalog = await metaService.connectProductCatalog(wabaId, catalogId)
    catalogs.value = sortCatalogs(catalogs.value.map(catalog => {
      if (catalog.id === connectedCatalog.id) return connectedCatalog
      return { ...catalog, is_associated: false }
    }))
    toastSuccess('Product catalog connected to the WABA')
    succeeded = true
  } catch (error) {
    connectError.value = getErrorMessage(error)
    toastError('Error connecting product catalog: ' + connectError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeConnectModal()
  }
}

function openDisconnectModal (catalog: ProductCatalog) {
  if (!canStartCatalogMutation() || !catalog.is_associated) return
  catalogToDisconnect.value = catalog
  disconnectError.value = ''
  disconnectModalOpen.value = true
}

function closeDisconnectModal () {
  if (isMutating.value) return
  disconnectModalOpen.value = false
  catalogToDisconnect.value = undefined
  disconnectError.value = ''
}

async function disconnectCatalog () {
  const selected = catalogToDisconnect.value
  if (!canStartCatalogMutation() || !selected?.is_associated) return
  activeAction.value = 'disconnect'
  disconnectError.value = ''
  let succeeded = false
  try {
    const service = await MetaService.getInstance()
    await service.disconnectProductCatalog(String(route.params.wabaId), selected.id)
    catalogs.value = catalogs.value.flatMap(catalog => {
      if (catalog.id !== selected.id) return [catalog]
      return catalog.is_owned ? [{ ...catalog, is_associated: false }] : []
    })
    toastSuccess('Product catalog disconnected from the WABA')
    succeeded = true
  } catch (error) {
    disconnectError.value = getErrorMessage(error)
    toastError('Error disconnecting product catalog: ' + disconnectError.value)
  } finally {
    activeAction.value = undefined
    if (succeeded) closeDisconnectModal()
  }
}

onMounted(async () => {
  await router.isReady()
  await loadPage()
})

return (_ctx: any,_cache: any) => {
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!loading.value && currentUser.value && whatsappBusinessAccount.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(BspPageHeader, {
            breadcrumbs: catalogBreadcrumbs.value,
            title: "Product catalogs",
            description: `Manage the Meta commerce catalogs available to ${whatsappBusinessAccount.value.name}.`,
            eyebrow: "Commerce",
            icon: "tabler:shopping-bag"
          }, {
            actions: _withCtx(() => [
              _createVNode(_unref(AButton), {
                variant: "outlined",
                color: "blue",
                "prepend-icon": "tabler:refresh",
                loading: refreshing.value,
                disabled: isMutating.value,
                onClick: refreshCatalogs
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode(" Refresh ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            meta: _withCtx(() => [
              _createVNode(_unref(ATag), {
                text: `WABA ${whatsappBusinessAccount.value.id}`,
                color: "info",
                type: "secondary"
              }, null, 8, ["text"]),
              _createVNode(_unref(ATag), {
                text: canManageCatalogs.value ? 'BSP administrator' : 'Read-only BSP access',
                color: canManageCatalogs.value ? 'success' : 'info',
                type: "secondary"
              }, null, 8, ["text", "color"])
            ]),
            _: 1
          }, 8, ["breadcrumbs", "description"]),
          _createElementVNode("section", _hoisted_3, [
            _createVNode(_unref(AInformationCard), {
              title: "Accessible catalogs",
              description: catalogCountLabel.value,
              "icon-name": "tabler:packages",
              variant: "flat"
            }, null, 8, ["description"]),
            _createVNode(_unref(AInformationCard), {
              title: "Connected catalog",
              description: associatedCatalog.value?.name || 'Connection not confirmed by Meta',
              "icon-name": "tabler:link",
              variant: "flat"
            }, null, 8, ["description"]),
            _createVNode(_unref(AInformationCard), {
              title: "Your BSP access",
              description: canManageCatalogs.value ? 'Administrator · Meta asset access required' : 'View and refresh only',
              "icon-name": "tabler:shield-lock",
              variant: "flat"
            }, null, 8, ["description"])
          ]),
          _createElementVNode("section", _hoisted_4, [
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "bsp-section-heading" }, [
              _createElementVNode("div", null, [
                _createElementVNode("span", { class: "bsp-section-heading__eyebrow" }, "Before you start"),
                _createElementVNode("h2", { id: "catalog-permissions-title" }, "Permissions and ownership"),
                _createElementVNode("p", null, "Meta validates the permissions granted to BSP and access to each catalog. A WABA can have only one connected catalog.")
              ])
            ], -1)),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(AIcon), {
                  icon: "tabler:key",
                  color: "primary",
                  size: "20",
                  "with-container": ""
                }),
                _cache[5] || (_cache[5] = _createElementVNode("div", null, [
                  _createElementVNode("h3", null, "Required Meta permissions"),
                  _createElementVNode("p", null, [
                    _createTextVNode(" Catalog management requires access to the catalog asset, "),
                    _createElementVNode("strong", null, "business_management"),
                    _createTextVNode(", and "),
                    _createElementVNode("strong", null, "catalog_management"),
                    _createTextVNode(". Connecting a catalog also requires "),
                    _createElementVNode("strong", null, "whatsapp_business_management"),
                    _createTextVNode(" and "),
                    _createElementVNode("strong", null, "whatsapp_business_messaging"),
                    _createTextVNode(". ")
                  ])
                ], -1))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_unref(AIcon), {
                  icon: "tabler:building-store",
                  color: "primary",
                  size: "20",
                  "with-container": ""
                }),
                _cache[6] || (_cache[6] = _createElementVNode("div", null, [
                  _createElementVNode("h3", null, "Business ownership"),
                  _createElementVNode("p", null, " Catalog ownership is resolved directly from Meta. Catalogs owned by another business remain read-only, and ownership cannot be transferred from this screen. ")
                ], -1))
              ])
            ])
          ]),
          _createElementVNode("section", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _cache[9] || (_cache[9] = _createElementVNode("div", null, [
                _createElementVNode("h2", { id: "catalog-access-title" }, "Catalog access shared with BSP"),
                _createElementVNode("p", null, "Link another existing commerce catalog from the business portfolio that owns this WABA. In Meta, keep the catalogs you already share selected and add the new catalogs. BSP preserves the existing WhatsApp connection. The authorization is available to other WABAs of this client in the same portfolio."),
                _createElementVNode("p", null, "Meta manages the catalog access granted to the connected app. To stop sharing a catalog, open Connected apps in Meta, select the BSP app, and remove only that catalog. Then refresh this page."),
                _createElementVNode("p", null, "Disconnect from WABA changes the WhatsApp association; it does not revoke the app's access or delete the catalog.")
              ], -1)),
              _createVNode(_unref(AButton), {
                color: "blue",
                "prepend-icon": "tabler:plus",
                loading: activeAction.value === 'authorize',
                disabled: writesBlocked.value || !_unref(catalogAuthorizationConfigId),
                onClick: authorizeCatalogs
              }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode(" Link new catalog ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            (!_unref(catalogAuthorizationConfigId))
              ? (_openBlock(), _createElementBlock("p", _hoisted_10, "Catalog authorization is not configured for this environment."))
              : _createCommentVNode("", true),
            (catalogAuthorizationError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(catalogAuthorizationError.value), 1))
              : _createCommentVNode("", true),
            (activeAction.value === 'authorize')
              ? (_openBlock(), _createElementBlock("p", _hoisted_12, "Complete the selection in Meta. Keep the existing catalogs selected; BSP will verify access before saving the catalog authorization."))
              : _createCommentVNode("", true),
            _createElementVNode("a", {
              href: metaCatalogAccessUrl.value,
              target: "_blank",
              rel: "noopener noreferrer",
              class: "bsp-catalog-access-link"
            }, [
              _createVNode(_unref(AIcon), {
                icon: "tabler:external-link",
                color: "primary",
                size: "18"
              }),
              _cache[10] || (_cache[10] = _createTextVNode(" Manage access in Meta ", -1))
            ], 8, _hoisted_13)
          ]),
          (!canManageCatalogs.value)
            ? (_openBlock(), _createElementBlock("section", _hoisted_14, [
                _createVNode(_unref(AIcon), {
                  icon: "tabler:eye",
                  color: "info",
                  size: "20",
                  "with-container": ""
                }),
                _cache[11] || (_cache[11] = _createElementVNode("div", null, [
                  _createElementVNode("h2", { id: "catalog-read-only-title" }, "Read-only access"),
                  _createElementVNode("p", null, "Only users with the admin or superuser role can change catalogs.")
                ], -1))
              ]))
            : _createCommentVNode("", true),
          (associatedCatalog.value)
            ? (_openBlock(), _createElementBlock("section", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("div", null, [
                    _cache[12] || (_cache[12] = _createElementVNode("h2", { id: "catalog-visibility-title" }, "WhatsApp catalog visibility", -1)),
                    _createElementVNode("p", null, _toDisplayString(associatedCatalog.value.name) + " · Control visibility for each number connected to this WABA.", 1)
                  ])
                ]),
                _createVNode(CatalogVisibilitySettings, {
                  "waba-id": String(_unref(route).params.wabaId),
                  "can-manage": canManageCatalogs.value,
                  disabled: refreshing.value || isMutating.value,
                  "initial-phone-id": String(_unref(route).query.phoneNumberId || ''),
                  "onUpdate:phoneId": rememberCatalogPhone
                }, null, 8, ["waba-id", "can-manage", "disabled", "initial-phone-id"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("section", {
            class: "bsp-section",
            "aria-busy": refreshing.value,
            "aria-labelledby": "catalog-list-title"
          }, [
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("div", null, [
                _createElementVNode("div", _hoisted_19, [
                  _cache[13] || (_cache[13] = _createElementVNode("h2", { id: "catalog-list-title" }, "Meta catalogs", -1)),
                  _createVNode(_unref(ATag), {
                    text: `${catalogs.value.length}`,
                    color: "primary",
                    type: "secondary"
                  }, null, 8, ["text"])
                ]),
                _cache[14] || (_cache[14] = _createElementVNode("p", null, "A business can own several catalogs. This WABA can connect only one; disconnect it before connecting another.", -1))
              ])
            ]),
            (loadError.value)
              ? (_openBlock(), _createBlock(_unref(AEmptyViewCard), {
                  key: 0,
                  icon: "tabler:cloud-off",
                  title: "Catalogs unavailable",
                  description: loadError.value,
                  "action-button-label": "Try again",
                  onAction: refreshCatalogs
                }, null, 8, ["description"]))
              : (catalogs.value.length === 0)
                ? (_openBlock(), _createBlock(_unref(AEmptyViewCard), {
                    key: 1,
                    icon: "tabler:shopping-bag-off",
                    title: "No accessible catalogs returned",
                    description: "An existing catalog may still be connected in Meta. Use Link new catalog to share an existing commerce catalog from this WABA’s business portfolio, then refresh. An empty response does not confirm that the WABA has no catalog."
                  }))
                : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(catalogs.value, (catalog) => {
                      return (_openBlock(), _createBlock(_component_v_card, {
                        key: catalog.id,
                        class: "bsp-catalog-card",
                        variant: "flat"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_21, [
                            _createElementVNode("div", _hoisted_22, [
                              _createVNode(_unref(AIcon), {
                                icon: "tabler:shopping-bag",
                                color: "primary",
                                size: "20",
                                "with-container": ""
                              }),
                              _createElementVNode("div", null, [
                                _createElementVNode("h3", null, _toDisplayString(catalog.name), 1),
                                _createElementVNode("span", null, "ID " + _toDisplayString(catalog.id), 1)
                              ])
                            ]),
                            _createElementVNode("div", _hoisted_23, [
                              (catalog.is_associated)
                                ? (_openBlock(), _createBlock(_unref(ATag), {
                                    key: 0,
                                    text: isCatalogOwned(catalog) ? 'Connected' : 'Connected · read-only',
                                    color: isCatalogOwned(catalog) ? 'success' : 'info',
                                    type: "secondary"
                                  }, null, 8, ["text", "color"]))
                                : (!isCatalogOwned(catalog))
                                  ? (_openBlock(), _createBlock(_unref(ATag), {
                                      key: 1,
                                      text: "Shared · read-only",
                                      color: "info",
                                      type: "secondary"
                                    }))
                                  : (_openBlock(), _createBlock(_unref(ATag), {
                                      key: 2,
                                      text: "Available",
                                      color: "primary",
                                      type: "secondary"
                                    }))
                            ])
                          ]),
                          _createElementVNode("dl", _hoisted_24, [
                            _createElementVNode("div", null, [
                              _cache[15] || (_cache[15] = _createElementVNode("dt", null, "Owner", -1)),
                              _createElementVNode("dd", null, _toDisplayString(catalog.owner_business.name), 1)
                            ]),
                            _createElementVNode("div", null, [
                              _cache[16] || (_cache[16] = _createElementVNode("dt", null, "Vertical", -1)),
                              _createElementVNode("dd", null, _toDisplayString(formatVertical(catalog.vertical)), 1)
                            ]),
                            _createElementVNode("div", null, [
                              _cache[17] || (_cache[17] = _createElementVNode("dt", null, "Products", -1)),
                              _createElementVNode("dd", null, _toDisplayString(formatCount(catalog.product_count)), 1)
                            ]),
                            _createElementVNode("div", null, [
                              _cache[18] || (_cache[18] = _createElementVNode("dt", null, "Feeds", -1)),
                              _createElementVNode("dd", null, _toDisplayString(formatCount(catalog.feed_count)), 1)
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_25, [
                            _createVNode(_unref(AButton), {
                              variant: "outlined",
                              color: "blue",
                              "prepend-icon": "tabler:packages",
                              disabled: isMutating.value,
                              onClick: ($event: any) => (_unref(router).push({ name: 'catalog-products', params: { wabaId: _unref(route).params.wabaId, catalogId: catalog.id } }))
                            }, {
                              default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                                _createTextVNode(" View products ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["disabled", "onClick"]),
                            (canManageCatalogs.value && isCatalogOwned(catalog))
                              ? (_openBlock(), _createBlock(_unref(AButton), {
                                  key: 0,
                                  variant: "outlined",
                                  color: "blue",
                                  "prepend-icon": "tabler:edit",
                                  disabled: writesBlocked.value,
                                  onClick: ($event: any) => (openEditModal(catalog))
                                }, {
                                  default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                    _createTextVNode(" Edit ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["disabled", "onClick"]))
                              : _createCommentVNode("", true),
                            (canManageCatalogs.value && !catalog.is_associated)
                              ? (_openBlock(), _createBlock(_unref(AButton), {
                                  key: 1,
                                  color: "blue",
                                  "prepend-icon": "tabler:link",
                                  disabled: isConnectDisabled(catalog),
                                  onClick: ($event: any) => (openConnectModal(catalog))
                                }, {
                                  default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                    _createTextVNode(" Connect to WABA ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["disabled", "onClick"]))
                              : _createCommentVNode("", true),
                            (canManageCatalogs.value && catalog.is_associated)
                              ? (_openBlock(), _createBlock(_unref(AButton), {
                                  key: 2,
                                  variant: "outlined",
                                  color: "red",
                                  "prepend-icon": "tabler:unlink",
                                  disabled: writesBlocked.value,
                                  onClick: ($event: any) => (openDisconnectModal(catalog))
                                }, {
                                  default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                    _createTextVNode(" Disconnect from WABA ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["disabled", "onClick"]))
                              : _createCommentVNode("", true)
                          ]),
                          (!isCatalogOwned(catalog))
                            ? (_openBlock(), _createElementBlock("p", _hoisted_26, " This catalog belongs to another business, so BSP cannot edit it. "))
                            : (associatedCatalog.value && !catalog.is_associated)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_27, " Another catalog is already connected. BSP will not replace it automatically. "))
                              : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ]))
          ], 8, _hoisted_17)
        ]))
      : (!loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
            _createVNode(BspPageHeader, {
              breadcrumbs: catalogBreadcrumbs.value,
              title: "Product catalogs",
              description: "The WhatsApp Business account is not available.",
              eyebrow: "Commerce",
              icon: "tabler:shopping-bag"
            }, null, 8, ["breadcrumbs"]),
            _createVNode(_unref(AEmptyViewCard), {
              icon: "tabler:alert-circle",
              title: "Catalogs could not be loaded",
              description: loadError.value || 'Return to the account and try again.',
              "action-button-label": "Try again",
              onAction: loadPage
            }, null, 8, ["description"])
          ]))
        : (_openBlock(), _createBlock(SpinnerLoader, { key: 2 })),
    _createVNode(_unref(AModal), {
      modelValue: formModalOpen.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formModalOpen).value = $event)),
      title: "Edit product catalog",
      "prepend-title-icon": "tabler:edit",
      width: "560px",
      "close-disabled": isMutating.value,
      onOnClose: closeFormModal
    }, {
      body: _withCtx(() => [
        _createElementVNode("form", {
          class: "bsp-modal-form",
          onSubmit: _withModifiers(submitCatalogForm, ["prevent"])
        }, [
          _cache[24] || (_cache[24] = _createElementVNode("p", null, "Change the name of this catalog. Meta requires catalog_management and permission to manage this catalog.", -1)),
          _createVNode(_unref(AInput), {
            modelValue: catalogName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((catalogName).value = $event)),
            label: "Catalog name",
            placeholder: "E.g. Chile commerce catalog",
            autocomplete: "off",
            required: "",
            disabled: refreshing.value || isMutating.value
          }, null, 8, ["modelValue", "disabled"]),
          _createElementVNode("div", _hoisted_29, [
            _createVNode(_unref(AIcon), {
              icon: "tabler:category",
              color: "primary",
              size: "18"
            }),
            _cache[23] || (_cache[23] = _createElementVNode("div", null, [
              _createElementVNode("span", null, "Vertical"),
              _createElementVNode("strong", null, "Commerce")
            ], -1))
          ]),
          _cache[25] || (_cache[25] = _createElementVNode("p", { class: "bsp-help-text" }, "The vertical is fixed when the catalog is created.", -1)),
          (formError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_30, _toDisplayString(formError.value), 1))
            : _createCommentVNode("", true)
        ], 32)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(AButton), {
          variant: "text",
          disabled: isMutating.value,
          onClick: closeFormModal
        }, {
          default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
            _createTextVNode("Cancel", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(AButton), {
          color: "blue",
          loading: activeAction.value === 'edit',
          disabled: !canSubmitCatalog.value || writesBlocked.value,
          onClick: submitCatalogForm
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(catalogFormButtonLabel.value), 1)
          ]),
          _: 1
        }, 8, ["loading", "disabled"])
      ]),
      _: 1
    }, 8, ["modelValue", "close-disabled"]),
    (catalogToConnect.value)
      ? (_openBlock(), _createBlock(_unref(AModal), {
          key: 3,
          modelValue: connectModalOpen.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((connectModalOpen).value = $event)),
          title: "Connect catalog to WABA",
          "prepend-title-icon": "tabler:link",
          width: "560px",
          "close-disabled": isMutating.value,
          onOnClose: closeConnectModal
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", _hoisted_31, [
              _createElementVNode("p", null, [
                _cache[27] || (_cache[27] = _createTextVNode(" Connect ", -1)),
                _createElementVNode("strong", null, _toDisplayString(catalogToConnect.value.name), 1),
                _cache[28] || (_cache[28] = _createTextVNode(" to ", -1)),
                _createElementVNode("strong", null, _toDisplayString(whatsappBusinessAccount.value?.name), 1),
                _cache[29] || (_cache[29] = _createTextVNode("? ", -1))
              ]),
              _createElementVNode("div", _hoisted_32, [
                _createVNode(_unref(AIcon), {
                  icon: "tabler:info-circle",
                  color: "info",
                  size: "18",
                  "with-container": ""
                }),
                _cache[30] || (_cache[30] = _createElementVNode("p", null, " A WABA supports one connected catalog. BSP does not replace an existing catalog or disconnect it automatically. ", -1))
              ]),
              (connectError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_33, _toDisplayString(connectError.value), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          actions: _withCtx(() => [
            _createVNode(_unref(AButton), {
              variant: "text",
              disabled: isMutating.value,
              onClick: closeConnectModal
            }, {
              default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_unref(AButton), {
              color: "blue",
              "prepend-icon": "tabler:link",
              loading: activeAction.value === 'connect',
              disabled: writesBlocked.value,
              onClick: connectCatalog
            }, {
              default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                _createTextVNode(" Connect catalog ", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"])
          ]),
          _: 1
        }, 8, ["modelValue", "close-disabled"]))
      : _createCommentVNode("", true),
    (catalogToDisconnect.value)
      ? (_openBlock(), _createBlock(_unref(AModal), {
          key: 4,
          modelValue: disconnectModalOpen.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((disconnectModalOpen).value = $event)),
          title: "Disconnect catalog from WABA",
          "prepend-title-icon": "tabler:unlink",
          width: "560px",
          "close-disabled": isMutating.value,
          onOnClose: closeDisconnectModal
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", _hoisted_34, [
              _createElementVNode("p", null, [
                _cache[33] || (_cache[33] = _createTextVNode("Disconnect ", -1)),
                _createElementVNode("strong", null, _toDisplayString(catalogToDisconnect.value.name), 1),
                _cache[34] || (_cache[34] = _createTextVNode(" from ", -1)),
                _createElementVNode("strong", null, _toDisplayString(whatsappBusinessAccount.value?.name), 1),
                _cache[35] || (_cache[35] = _createTextVNode("?", -1))
              ]),
              _cache[36] || (_cache[36] = _createElementVNode("p", null, "The catalog and its products will be retained in Meta. Products from this catalog will no longer be available through this WABA until a catalog is connected again.", -1)),
              (disconnectError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_35, _toDisplayString(disconnectError.value), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          actions: _withCtx(() => [
            _createVNode(_unref(AButton), {
              variant: "text",
              disabled: isMutating.value,
              onClick: closeDisconnectModal
            }, {
              default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_unref(AButton), {
              color: "red",
              loading: activeAction.value === 'disconnect',
              disabled: writesBlocked.value,
              onClick: disconnectCatalog
            }, {
              default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                _createTextVNode("Disconnect catalog", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"])
          ]),
          _: 1
        }, 8, ["modelValue", "close-disabled"]))
      : _createCommentVNode("", true)
  ]))
}
}

})